import { useId, useState } from "react";
import { Calculator } from "lucide-react";
import { useContent } from "@/data/content";
import { formatEuro, usePricing } from "@/data/pricing";
import { useLocale } from "@/i18n/useLocale";

const DEFAULT_TICKET = 40;

/**
 * Plain arithmetic, not a forecast: how many new bookings at the visitor's
 * own average price cover a monthly plan.
 */
export function RoiCalculator() {
  const { roi } = useContent().home;
  const pricing = usePricing();
  const locale = useLocale();
  const ticketId = useId();
  const resultId = useId();
  const monthly = pricing.packs.filter((pack) => pack.cadence === "month");
  const [packId, setPackId] = useState(monthly[0]?.id);
  const [ticket, setTicket] = useState(String(DEFAULT_TICKET));

  const pack = monthly.find((p) => p.id === packId) ?? monthly[0];
  if (!pack) return null;

  const amount = Number(ticket.replace(",", "."));
  const valid = Number.isFinite(amount) && amount > 0;
  const count = valid ? Math.ceil(pack.price / amount) : null;
  const result =
    count === null
      ? null
      : (count === 1 ? roi.resultOne : roi.resultMany).replace("{count}", String(count)).replace("{plan}", pack.name);

  return (
    <div
      data-reveal=""
      className="mx-auto mt-14 max-w-4xl rounded-[1.75rem] border border-brand-primary/10 bg-brand-bg p-7 sm:p-9"
    >
      <div className="flex gap-4">
        <Calculator aria-hidden="true" className="mt-1 size-6 shrink-0 text-teal-strong" />
        <div>
          <h3 className="font-display text-xl font-semibold text-brand-primary sm:text-2xl">{roi.title}</h3>
          <p className="mt-2 leading-relaxed text-ink-soft">{roi.lead}</p>
        </div>
      </div>

      <div className="mt-7 grid gap-6 md:grid-cols-[minmax(0,14rem)_1fr] md:items-end">
        <div>
          <label htmlFor={ticketId} className="block text-sm font-semibold text-brand-primary">
            {roi.ticketLabel}
          </label>
          <input
            id={ticketId}
            type="number"
            inputMode="decimal"
            min={1}
            step={1}
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
            aria-describedby={resultId}
            className="mt-2 w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-lg font-semibold text-brand-primary outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40"
          />
        </div>

        <fieldset>
          <legend className="text-sm font-semibold text-brand-primary">{roi.planLabel}</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {monthly.map((option) => (
              <label
                key={option.id}
                className="cursor-pointer rounded-full border border-brand-primary/15 bg-white px-4 py-2.5 text-sm text-ink-soft transition-colors hover:border-teal-strong has-[:checked]:border-brand-primary has-[:checked]:bg-brand-primary has-[:checked]:text-white has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-teal-strong"
              >
                <input
                  type="radio"
                  name={`${ticketId}-plan`}
                  value={option.id}
                  checked={option.id === pack.id}
                  onChange={() => setPackId(option.id)}
                  className="sr-only"
                />
                <span className="font-semibold">{option.name}</span> · {formatEuro(option.price, locale)}{" "}
                {pricing.perMonth}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <p
        id={resultId}
        aria-live="polite"
        className="mt-7 font-display text-2xl font-semibold leading-snug text-brand-primary sm:text-[1.875rem]"
      >
        {result ?? "—"}
      </p>
      <p className="mt-3 text-sm text-ink-muted">{roi.note}</p>
    </div>
  );
}
